import { Container } from "@mui/material";
import {
  SmartCheckbox,
  SmartForm,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BerthDetailsDto, Coordinate } from "~/api/GeneratedApi";

const dataToForm = (
  x: BerthDetailsDto | undefined,
  portId?: number,
): BerthFormInputs => {
  return {
    ...x,
    id: x?.id ?? -1,
    capacity: x?.capacity ?? 0,
    water: x?.water ?? false,
    garbage: x?.garbage ?? false,
    power: x?.power ?? false,
    portId: x?.portId ?? portId ?? null,
    description: x?.description ?? "",
    name: x?.name ?? "",
    points: x?.points ?? [],
  };
};

export interface BerthFormInputs {
  id: number;
  portId: number | null;
  name: string;
  description: string;
  capacity: number;
  water: boolean;
  garbage: boolean;
  power: boolean;
  points: Coordinate[];
}

interface Props {
  onSubmit: (data: BerthFormInputs) => void;
  readOnly?: boolean;
  small?: boolean;
  defaultValues?: BerthDetailsDto;
  formId: string;
  portId?: number;
}

const BerthForm = ({
  onSubmit,
  readOnly,
  small,
  formId,
  defaultValues,
  portId,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("berths");

  const formDataDefaultValues = useMemo(
    () => dataToForm(defaultValues, portId),
    [defaultValues, portId],
  );

  const formMethods = useSmartForm({
    defaultValues: formDataDefaultValues,
    mode: "onSubmit",
  });

  return (
    <Container maxWidth={false}>
      <SmartForm<BerthFormInputs>
        onSubmit={onSubmit}
        gridProps={{ justifyContent: "center", direction: "column" }}
        methods={formMethods}
        formId={formId}
      >
        <SmartTextField<BerthFormInputs>
          name="name"
          label={t("name")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          required
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
          autoFocus
        />
        <SmartTextField<BerthFormInputs>
          name="description"
          label={t("description")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
          autoFocus
        />
        <SmartCheckbox name="power" label={t("power")} disabled={readOnly} />
        <SmartCheckbox
          name="garbage"
          label={t("garbage")}
          disabled={readOnly}
        />
        <SmartCheckbox name="water" label={t("water")} disabled={readOnly} />
        <SmartTextField<BerthFormInputs>
          name="capacity"
          label={t("capacity")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          required
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
          autoFocus
        />
      </SmartForm>
    </Container>
  );
};

export default BerthForm;
